import { useCallback, useEffect, useState } from "react";
import insightsApi from "../api/insightsApi";

// Every project the signed-in user can see, for a project picker.
//
// The backend caps page_size, so requesting page_size: 100 isn't enough to get
// every project — this pages through `next` until exhausted. Lifted out of the
// Documents page, which had the only picker; the survey builder now links an
// assessment to a project with the same list. That list includes a project with no waves yet, to its organization — the one an
// assessment is about to be linked to.
//
// `enabled: false` skips the fetch (the Documents page's project tab, which
// already knows its project). `onError` is told about a failed fetch; the list
// is simply left empty.
//
// `withAreas: true` (`?with_areas=1`) lists only projects with at least one
// area — the ones an assessment can usefully be linked to as its impact
// assessment. The Documents page keeps the full list.
export default function useProjects({ enabled = true, withAreas = false, onError } = {}) {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(false);

    const reload = useCallback(async () => {
        try {
            setLoading(true);
            const all = [];
            let page = 1;
            // Hard cap on pages as a safety net against an unexpected loop.
            while (page <= 100) {
                const data = await insightsApi.getProjects({ page, page_size: 100, ...(withAreas ? { with_areas: 1 } : {}) });
                all.push(...(Array.isArray(data) ? data : data?.results || []));
                if (Array.isArray(data) || !data?.next) break;
                page += 1;
            }
            setProjects(all);
            return all;
        } catch (error) {
            console.error("Failed to load projects:", error);
            onError?.(error);
            return [];
        } finally {
            setLoading(false);
        }
        // onError is a notification, not an input: a new function identity from
        // the caller must not refetch the list.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [withAreas]);

    useEffect(() => {
        if (enabled) reload();
    }, [enabled, reload]);

    return { projects, loading, reload };
}
